// Versão: 1.00
// Data..: 2026-10-01
// Página pública de pagamento, acessada por token (sem login), em /portal/pagar/:token.
// Mostra os dados da cobrança (valor, vencimento, descrição, cliente, situação) e,
// quando a cobrança está em aberto, os meios de pagamento: PIX (QR Code gerado no
// cliente + copiar o copia-e-cola) e boleto (linha digitável + copiar + baixar PDF).
// Quando a cobrança já está paga, exibe apenas a confirmação.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { PublicShell } from './PublicShell.js';
import { publicApi, type PublicPaymentView } from '../../lib/publicApi.js';
import { config } from '../../config.js';

// Formata moeda BRL (igual ao de InvoicesPage).
const brl = (n?: number) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Formata AAAA-MM-DD como dd/mm/aaaa (sem fuso, igual ao de InvoicesPage).
const dateBr = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Classe de badge por situação (reusa as cores das badges existentes).
const statusBadge: Record<string, string> = {
  draft: 'badge-normal',
  issued: 'badge-open',
  paid: 'badge-closed',
  overdue: 'badge-high',
  canceled: 'badge-normal',
};

// Rótulo pt-BR por situação.
const statusLabel: Record<string, string> = {
  draft: 'Rascunho',
  issued: 'Em aberto',
  paid: 'Paga',
  overdue: 'Vencida',
  canceled: 'Cancelada',
};

export function PublicPaymentPage() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PublicPaymentView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'pix' | 'boleto' | null>(null);

  useEffect(() => {
    if (!token) return;
    publicApi.getPayment(token)
      .then((v) => setView(v))
      .catch((e) => setError(e instanceof Error ? e.message : 'Link de pagamento inválido ou expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Copia um texto para a área de transferência e dá feedback local temporário.
  const copy = async (text: string, which: 'pix' | 'boleto') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('Não foi possível copiar. Copie manualmente.');
    }
  };

  // Abre o PDF do boleto (rota pública, SEM Authorization) como blob em nova aba.
  const downloadPdf = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${config.apiUrl}/public/pay/${token}/pdf`);
      if (!res.ok) throw new Error('Não foi possível carregar o boleto.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao abrir o boleto.');
    }
  };

  return (
    <PublicShell>
      <div style={{ maxWidth: 560, margin: '48px auto 64px', padding: '0 24px' }}>
        {loading ? (
          <div className="flex justify-center" style={{ padding: 48 }}><span className="spinner" /></div>
        ) : error && !view ? (
          <div className="card empty"><div className="empty-icon">🔍</div>{error}</div>
        ) : !view ? (
          <div className="card empty"><div className="empty-icon">🔍</div>Link de pagamento inválido ou expirado.</div>
        ) : (
          <div className="card fade-in">
            <div className="flex items-center justify-between">
              <div className="text-dim text-sm">Cobrança nº {view.number}</div>
              <span className={`badge ${statusBadge[view.status] ?? 'badge-normal'}`}>
                {statusLabel[view.status] ?? view.status}
              </span>
            </div>

            <h1 style={{ margin: '8px 0 2px' }}>{brl(view.amount)}</h1>
            <p className="text-muted" style={{ marginTop: 0 }}>{view.description}</p>

            <div className="flex justify-between mt-2">
              <span className="text-dim text-sm">Cliente</span>
              <span className="text-sm">{view.clientName}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-dim text-sm">Vencimento</span>
              <span className="text-sm">{dateBr(view.dueDate)}</span>
            </div>

            {view.paid ? (
              <div className="mt-4" style={{ textAlign: 'center', borderTop: '1px solid var(--border)', paddingTop: 'var(--s3)' }}>
                <div style={{ fontSize: 44 }}>✅</div>
                <h2 style={{ margin: '4px 0' }}>Pagamento confirmado</h2>
                <p className="text-muted">Esta cobrança já foi paga. Obrigado!</p>
              </div>
            ) : (
              <>
                {/* Bloco PIX — só quando há copia-e-cola. */}
                {view.pixCopiaECola && (
                  <div className="mt-4" style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--s3)' }}>
                    <h3 style={{ marginTop: 0 }}>Pagar com PIX</h3>
                    <p className="text-dim text-sm">Escaneie o QR Code no app do seu banco ou copie o código.</p>
                    <div
                      className="flex justify-center"
                      style={{ background: '#fff', padding: 16, borderRadius: 10, margin: '12px 0' }}
                    >
                      <QRCode value={view.pixCopiaECola} size={180} />
                    </div>
                    <button className="btn btn-primary w-full" type="button" onClick={() => copy(view.pixCopiaECola!, 'pix')}>
                      {copied === 'pix' ? '✓ Código copiado' : 'Copiar código PIX'}
                    </button>
                  </div>
                )}

                {/* Bloco boleto — quando há linha digitável ou PDF disponível. */}
                {(view.linhaDigitavel || view.boletoPdfAvailable) && (
                  <div className="mt-4" style={{ borderTop: '1px solid var(--border)', paddingTop: 'var(--s3)' }}>
                    <h3 style={{ marginTop: 0 }}>Pagar com boleto</h3>
                    {view.linhaDigitavel && (
                      <>
                        <div
                          className="text-sm"
                          style={{ fontFamily: 'monospace', wordBreak: 'break-all', background: 'var(--surface)', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }}
                        >
                          {view.linhaDigitavel}
                        </div>
                        <button className="btn w-full mt-2" type="button" onClick={() => copy(view.linhaDigitavel!, 'boleto')}>
                          {copied === 'boleto' ? '✓ Linha copiada' : 'Copiar linha digitável'}
                        </button>
                      </>
                    )}
                    {view.boletoPdfAvailable && (
                      <button className="btn btn-secondary w-full mt-2" type="button" onClick={downloadPdf}>
                        📄 Baixar boleto PDF
                      </button>
                    )}
                  </div>
                )}

                {error && <div className="text-sm mt-2" style={{ color: 'var(--danger)' }}>{error}</div>}
              </>
            )}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
